import React, { useState, useEffect, useRef } from 'react';
import { X, Plus } from 'lucide-react';

interface SkillChipInputProps {
  label: string;
  skills: string[];
  onSkillsChange: (skills: string[]) => void;
  existingSkills: string[];
  placeholder?: string;
}

export const SkillChipInput: React.FC<SkillChipInputProps> = ({
  label,
  skills,
  onSkillsChange,
  existingSkills,
  placeholder
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputValue.trim().length > 0) {
      const filtered = existingSkills
        .filter(skill => 
          skill.toLowerCase().includes(inputValue.toLowerCase()) &&
          !skills.includes(skill)
        )
        .slice(0, 8);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  }, [inputValue, skills, existingSkills]);

  const addSkill = (skill: string) => {
    const trimmedSkill = skill.trim();
    if (trimmedSkill && !skills.includes(trimmedSkill)) {
      onSkillsChange([...skills, trimmedSkill]);
      setInputValue('');
      setShowSuggestions(false);
    }
  };

  const removeSkill = (skillToRemove: string) => {
    onSkillsChange(skills.filter(skill => skill !== skillToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && filteredSuggestions.length > 0) {
        addSkill(filteredSuggestions[0]);
      } else if (inputValue.trim()) {
        addSkill(inputValue);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    } else if (e.key === 'Backspace' && !inputValue && skills.length > 0) {
      // Remove last skill if input is empty and backspace is pressed
      removeSkill(skills[skills.length - 1]);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    addSkill(suggestion);
    inputRef.current?.focus();
  };

  const isExistingSkill = (skill: string) => {
    return existingSkills.includes(skill);
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      
      {/* Input Field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (inputValue.trim().length > 0) setShowSuggestions(true);
          }}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          className="w-full p-3 border rounded-lg"
          placeholder={placeholder || `Add ${label.toLowerCase()}...`}
        />
        
        {/* Add Button */}
        {inputValue.trim() && (
          <button
            type="button"
            onClick={() => addSkill(inputValue)}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-primary transition-colors"
          >
            <Plus size={20} />
          </button>
        )}
        
        {/* Suggestions Dropdown */}
        {showSuggestions && filteredSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg mt-1 z-10 max-h-48 overflow-y-auto">
            {filteredSuggestions.map((suggestion, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleSuggestionClick(suggestion)}
                className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-center justify-between text-sm"
              >
                <span>{suggestion}</span>
                {index === 0 && (
                  <span className="text-xs text-gray-400">Press Enter</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Skill Chips */}
      {skills.length > 0 && (
        <div className="mt-3">
          <div className="flex flex-wrap gap-2">
            {skills.map((skill) => (
              <span
                key={skill}
                className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                  isExistingSkill(skill)
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-green-100 text-green-800 border border-green-200'
                }`}
              >
                {skill}
                <button
                  type="button"
                  onClick={() => removeSkill(skill)}
                  className="hover:text-red-600 transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
              Existing skills
            </span>
            <span className="ml-4 inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              New skills
            </span>
          </p>
        </div>
      )}

      {/* Help Text */}
      <p className="text-xs text-muted-foreground mt-1">
        Type to see suggestions, press Enter to add new skills, or click suggestions to add existing ones.
      </p>
    </div>
  );
};